// Browser-Druck-Zertifikate: erzeugt eine eigenständige HTML-Seite in einem neuen
// Fenster und öffnet den Druck-Dialog. Der Nutzer speichert über "Als PDF speichern".
// Kein Backend, keine externe API nötig.

export interface CertItem {
  label: string
  type: string // 'Kurs' | 'Unterweisung'
  date: string // bereits formatiert (dd.MM.yyyy)
}

const BRAND = '#0D5295'
const BRAND_DEEP = '#0A395B'
const INK = '#23282D'
const MUTED = '#60666C'
const LINE = '#E4DFDD'

function esc(v: string): string {
  return String(v == null ? '' : v)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
}

function shell(title: string, body: string): string {
  return `<!doctype html>
<html lang="de"><head><meta charset="utf-8"><title>${esc(title)}</title>
<style>
  * { box-sizing: border-box; }
  body { margin: 0; font-family: 'Inter','Helvetica Neue',Arial,sans-serif; color: ${INK}; background: #f4f4f5; }
  .toolbar { position: sticky; top: 0; padding: 12px 16px; background: #fff; border-bottom: 1px solid ${LINE}; text-align: center; }
  .toolbar button { font: inherit; font-weight: 600; background: ${BRAND}; color: #fff; border: none; border-radius: 8px; padding: 10px 20px; cursor: pointer; }
  .page { max-width: 820px; margin: 24px auto; background: #fff; padding: 56px 64px; border: 1px solid ${LINE}; }
  .brand { font-family: 'Poppins','Inter',sans-serif; font-weight: 700; font-size: 20px; color: ${BRAND}; letter-spacing: .3px; }
  .brand small { display:block; font-size: 11px; color: ${MUTED}; font-weight: 600; letter-spacing: 2px; text-transform: uppercase; }
  h1 { font-family: 'Poppins','Inter',sans-serif; font-size: 30px; color: ${BRAND_DEEP}; margin: 36px 0 6px; }
  .eyebrow { color: ${BRAND}; font-weight: 700; text-transform: uppercase; letter-spacing: 1.5px; font-size: 12px; }
  .lead { font-size: 16px; color: ${INK}; margin: 14px 0 6px; }
  .name { font-size: 24px; font-weight: 700; color: ${BRAND_DEEP}; margin: 6px 0 24px; }
  table { width: 100%; border-collapse: collapse; margin: 12px 0 8px; }
  th, td { text-align: left; padding: 10px 12px; border-bottom: 1px solid ${LINE}; font-size: 14px; }
  th { color: ${MUTED}; text-transform: uppercase; letter-spacing: .04em; font-size: 11px; }
  .meta { margin-top: 36px; color: ${MUTED}; font-size: 13px; display:flex; justify-content: space-between; gap: 24px; }
  .empty { color: ${MUTED}; font-style: italic; }
  @media print {
    body { background: #fff; }
    .toolbar { display: none; }
    .page { margin: 0; border: none; max-width: none; padding: 24px 28px; }
  }
</style></head>
<body>
  <div class="toolbar"><button onclick="window.print()">Drucken / als PDF speichern</button></div>
  ${body}
</body></html>`
}

function itemsTable(items: CertItem[]): string {
  if (!items.length) return '<p class="empty">Noch keine abgeschlossenen Kurse oder Unterweisungen.</p>'
  const rows = items
    .map((i) => `<tr><td>${esc(i.label)}</td><td>${esc(i.type)}</td><td>${esc(i.date)}</td></tr>`)
    .join('')
  return `<table><thead><tr><th>Titel</th><th>Art</th><th>Abgeschlossen am</th></tr></thead><tbody>${rows}</tbody></table>`
}

function openPrintWindow(html: string) {
  const w = window.open('', '_blank')
  if (!w) {
    window.alert('Bitte erlauben Sie Popups, um das Zertifikat zu öffnen.')
    return
  }
  w.document.open()
  w.document.write(html)
  w.document.close()
  w.focus()
  // Druck-Dialog nach dem Laden auslösen (mit Fallback)
  const triggerPrint = () => { try { w.print() } catch (e) { /* ignore */ } }
  w.onload = triggerPrint
  setTimeout(triggerPrint, 600)
}

/**
 * Teilnahmebescheinigung für eine Person – ein oder mehrere abgeschlossene Einträge
 * (Sammel-Zertifikat = mehrere Items, Einzel-Zertifikat = ein Item).
 */
export function printCertificate(opts: { orgName: string; employeeName: string; items: CertItem[]; dateStr: string }) {
  const items = opts.items
  const n = items.length
  const onlyInstructions = n > 0 && items.every((i) => i.type === 'Unterweisung')
  const onlyCourses = n > 0 && items.every((i) => i.type === 'Kurs')

  let heading: string
  let leadAfterName: string
  if (onlyInstructions) {
    heading = n === 1 ? 'Nachweis der Unterweisung' : 'Nachweis der Unterweisungen'
    leadAfterName = n === 1
      ? 'die folgende Unterweisung gelesen und bestätigt hat:'
      : 'die folgenden Unterweisungen gelesen und bestätigt hat:'
  } else if (onlyCourses) {
    heading = 'Teilnahmebescheinigung'
    leadAfterName = n === 1
      ? 'die folgende Fortbildung erfolgreich abgeschlossen hat:'
      : 'die folgenden Fortbildungen erfolgreich abgeschlossen hat:'
  } else {
    heading = 'Teilnahmenachweis'
    leadAfterName = 'die folgenden Kurse abgeschlossen und Unterweisungen bestätigt hat:'
  }

  const body = `
    <div class="page">
      <div class="brand">MEDA<small>Fortbildungen</small></div>
      <div class="eyebrow" style="margin-top:32px;">${esc(opts.orgName)}</div>
      <h1>${heading}</h1>
      <p class="lead">Hiermit wird bestätigt, dass</p>
      <div class="name">${esc(opts.employeeName)}</div>
      <p class="lead">${leadAfterName}</p>
      ${itemsTable(opts.items)}
      <div class="meta">
        <span>${esc(opts.orgName)}</span>
        <span>Ausgestellt am ${esc(opts.dateStr)}</span>
      </div>
    </div>`
  openPrintWindow(shell(heading + ' – ' + opts.employeeName, body))
}

/**
 * Nachweis einer einzelnen Unterweisungs-Bestätigung: wer hat wann was
 * bestätigt, mit welchen Dokumenten und Punkten. Ersetzt die serverseitige
 * PDF-Erzeugung — der Beweis ist der gespeicherte Bestätigungsdatensatz,
 * gedruckt wird über den Browser ("Als PDF speichern").
 */
export function printInstructionConfirmation(opts: {
  orgName: string
  employeeName: string
  signatureName: string
  instructionTitle: string
  documentTitles: string[]
  confirmedAt: number
  checkedItems?: { label: string; checked: boolean }[]
}) {
  const confirmedDate = new Date(opts.confirmedAt)
  const dateStr = confirmedDate.toLocaleDateString('de-DE')
  const timeStr = confirmedDate.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })

  const docList = opts.documentTitles.length
    ? `<h2 style="font-family:'Poppins',sans-serif;font-size:14px;color:${BRAND_DEEP};margin:24px 0 4px;">Enthaltene Dokumente</h2>
       <table><tbody>${opts.documentTitles.map((t) => `<tr><td>${esc(t)}</td></tr>`).join('')}</tbody></table>`
    : ''

  const items = (opts.checkedItems || []).filter((i) => i.checked)
  const itemList = items.length
    ? `<h2 style="font-family:'Poppins',sans-serif;font-size:14px;color:${BRAND_DEEP};margin:24px 0 4px;">Bestätigte Punkte</h2>
       <table><tbody>${items.map((i) => `<tr><td>✓ ${esc(i.label)}</td></tr>`).join('')}</tbody></table>`
    : ''

  const body = `
    <div class="page">
      <div class="brand">MEDA<small>Fortbildungen</small></div>
      <div class="eyebrow" style="margin-top:32px;">${esc(opts.orgName)}</div>
      <h1>Nachweis der Unterweisung</h1>
      <p class="lead">Hiermit wird bestätigt, dass</p>
      <div class="name">${esc(opts.employeeName)}</div>
      <p class="lead">die Unterweisung <strong>„${esc(opts.instructionTitle)}"</strong> am ${esc(dateStr)} um ${esc(timeStr)} Uhr gelesen und digital bestätigt hat.</p>
      ${docList}
      ${itemList}
      <div style="margin-top:36px;">
        <div style="font-size:11px;text-transform:uppercase;letter-spacing:1px;color:${MUTED};margin-bottom:6px;">Digitale Unterschrift</div>
        <div style="font-family:'Brush Script MT','Segoe Script',cursive;font-size:26px;color:${BRAND_DEEP};font-style:italic;">${esc(opts.signatureName)}</div>
      </div>
      <div class="meta">
        <span>${esc(opts.orgName)}</span>
        <span>Bestätigt am ${esc(dateStr)}</span>
      </div>
    </div>`
  openPrintWindow(shell('Nachweis – ' + opts.instructionTitle, body))
}

/**
 * Gesamtübersicht für die ganze Einrichtung: alle Mitarbeitenden mit ihren
 * abgeschlossenen Kursen/Unterweisungen.
 */
export function printOrgOverview(opts: {
  orgName: string
  year: number | string
  dateStr: string
  rows: { name: string; items: CertItem[] }[]
}) {
  const sections = opts.rows
    .map((r) => `
      <h2 style="font-family:'Poppins',sans-serif;font-size:16px;color:${BRAND_DEEP};margin:24px 0 4px;">${esc(r.name)}</h2>
      ${itemsTable(r.items)}`)
    .join('')
  const body = `
    <div class="page">
      <div class="brand">MEDA<small>Fortbildungen</small></div>
      <div class="eyebrow" style="margin-top:32px;">${esc(opts.orgName)}</div>
      <h1>Abschluss-Übersicht ${esc(String(opts.year))}</h1>
      ${sections || '<p class="empty">Keine Daten vorhanden.</p>'}
      <div class="meta"><span>${esc(opts.orgName)}</span><span>Ausgestellt am ${esc(opts.dateStr)}</span></div>
    </div>`
  openPrintWindow(shell('Abschluss-Übersicht – ' + opts.orgName, body))
}
